import type { PostHog } from 'posthog-js';

type FeatureFlagValue = string | boolean | undefined;
type QueuedWrite = (client: PostHog) => void;

const MAX_QUEUED_WRITES = 200;

let posthogClient: PostHog | null = null;
let initializationPromise: Promise<PostHog | null> | null = null;
let idleInitializationHandle: number | null = null;
let idleInitializationKind: 'idle' | 'timeout' | null = null;
let warnedAboutMissingKey = false;
const queuedWrites: QueuedWrite[] = [];
const requestedFeatureFlags = new Set<string>();
const featureFlagCache = new Map<string, FeatureFlagValue>();

function getPostHogKey(): string | undefined {
  return process.env.NEXT_PUBLIC_POSTHOG_KEY;
}

function warnAboutMissingKey(): void {
  if (warnedAboutMissingKey) return;
  warnedAboutMissingKey = true;
  console.warn('PostHog key not found. Product analytics will not be tracked.');
}

function runSafely(write: QueuedWrite, client: PostHog): void {
  try {
    write(client);
  } catch (error) {
    console.warn('PostHog operation failed.', error);
  }
}

function flushQueuedWrites(client: PostHog): void {
  const writes = queuedWrites.splice(0, queuedWrites.length);
  for (const write of writes) {
    runSafely(write, client);
  }
}

function refreshRequestedFeatureFlags(client: PostHog): void {
  for (const flagKey of requestedFeatureFlags) {
    featureFlagCache.set(flagKey, client.getFeatureFlag(flagKey));
  }
}

function enqueueWrite(write: QueuedWrite): void {
  if (typeof window === 'undefined') return;

  if (posthogClient) {
    runSafely(write, posthogClient);
    return;
  }

  if (!getPostHogKey()) {
    warnAboutMissingKey();
    return;
  }

  if (queuedWrites.length >= MAX_QUEUED_WRITES) {
    queuedWrites.shift();
  }
  queuedWrites.push(write);
  schedulePostHogInitialization();
}

export function initPostHog(): Promise<PostHog | null> {
  if (typeof window === 'undefined') return Promise.resolve(null);
  if (posthogClient) return Promise.resolve(posthogClient);
  if (initializationPromise) return initializationPromise;

  const key = getPostHogKey();
  if (!key) {
    warnAboutMissingKey();
    queuedWrites.length = 0;
    return Promise.resolve(null);
  }

  const host = process.env.NEXT_PUBLIC_POSTHOG_HOST || 'https://us.i.posthog.com';
  initializationPromise = import('posthog-js')
    .then(({ default: posthog }) => {
      posthog.init(key, {
        api_host: host,
        person_profiles: 'identified_only',
        capture_pageview: false,
        capture_pageleave: true,
        ...(process.env.NODE_ENV === 'development' ? { capture_performance: false } : {}),
        session_recording: {
          recordCrossOriginIframes: true,
        },
        autocapture: {
          dom_event_allowlist: ['click', 'change', 'submit'],
        },
        loaded: client => {
          if (process.env.NODE_ENV === 'development') {
            client.debug();
          }
        },
      });

      posthogClient = posthog;
      posthog.onFeatureFlags(() => refreshRequestedFeatureFlags(posthog));
      flushQueuedWrites(posthog);
      refreshRequestedFeatureFlags(posthog);
      return posthog;
    })
    .catch(error => {
      console.warn('PostHog failed to initialize.', error);
      return null;
    })
    .finally(() => {
      initializationPromise = null;
    });

  return initializationPromise;
}

export function schedulePostHogInitialization(): void {
  if (
    typeof window === 'undefined' ||
    posthogClient ||
    initializationPromise ||
    idleInitializationHandle !== null
  ) {
    return;
  }

  const initialize = () => {
    idleInitializationHandle = null;
    idleInitializationKind = null;
    void initPostHog();
  };

  if (typeof window.requestIdleCallback === 'function') {
    idleInitializationKind = 'idle';
    idleInitializationHandle = window.requestIdleCallback(initialize, { timeout: 2_000 });
  } else {
    idleInitializationKind = 'timeout';
    idleInitializationHandle = window.setTimeout(initialize, 1_000);
  }
}

export function cancelScheduledPostHogInitialization(): void {
  if (typeof window === 'undefined' || idleInitializationHandle === null) return;

  if (idleInitializationKind === 'idle' && typeof window.cancelIdleCallback === 'function') {
    window.cancelIdleCallback(idleInitializationHandle);
  } else {
    window.clearTimeout(idleInitializationHandle);
  }
  idleInitializationHandle = null;
  idleInitializationKind = null;
}

export function getPostHog(): PostHog | null {
  return typeof window === 'undefined' ? null : posthogClient;
}

export function capture(eventName: string, properties?: Record<string, unknown>): void {
  enqueueWrite(client => client.capture(eventName, properties));
}

export function identify(userId: string, properties?: Record<string, unknown>): void {
  enqueueWrite(client => client.identify(userId, properties));
}

export function reset(): void {
  featureFlagCache.clear();
  requestedFeatureFlags.clear();
  enqueueWrite(client => client.reset());
}

export function isFeatureEnabled(flagKey: string): boolean {
  return Boolean(getFeatureFlag(flagKey));
}

export function getFeatureFlag(flagKey: string): FeatureFlagValue {
  requestedFeatureFlags.add(flagKey);
  if (!posthogClient) {
    schedulePostHogInitialization();
    return featureFlagCache.get(flagKey);
  }

  try {
    const value = posthogClient.getFeatureFlag(flagKey);
    featureFlagCache.set(flagKey, value);
    return value;
  } catch {
    return featureFlagCache.get(flagKey);
  }
}

export function capturePageView(properties?: Record<string, unknown>): void {
  enqueueWrite(client => client.capture('$pageview', properties));
}

export function group(
  groupType: string,
  groupKey: string,
  properties?: Record<string, unknown>
): void {
  enqueueWrite(client => client.group(groupType, groupKey, properties));
}

export function resetGroups(): void {
  enqueueWrite(client => client.resetGroups());
}

export function setPersonProperties(properties: Record<string, unknown>): void {
  enqueueWrite(client => client.setPersonProperties(properties));
}

export function alias(aliasId: string): void {
  enqueueWrite(client => client.alias(aliasId));
}
